// Motor de análisis instanciable.
//
// Reemplaza conceptualmente el estado global mutable del Analizador
// original (let M,cur,timer,bf,cnt,mx,PT en public/cabrillo/src/plantilla.html)
// por una fábrica de instancias independientes: crearAnalizador(modelo).
// Cada instancia tiene su propio cursor de tiempo, filtro de banda,
// velocidad y estado de reproducción — es lo que habilita a futuro
// `const A = crearAnalizador(modeloA); const B = crearAnalizador(modeloB)`
// sin compartir nada entre sí, sin duplicar la app y sin iframes.
//
// Sin DOM: no hace document.querySelector ni toca canvas/botones. Expone
// datos (subconjunto visible de QSO, acumulados, estadísticas) que la
// capa de render (src/cabrillo/render/*.js) consume — ver "render ≠ motor"
// en esos módulos.
//
// Basado en el Analizador Cabrillo original de XQ2OP (set/load/pickBand/
// stop/stats/kpis de public/cabrillo/src/plantilla.html). Ver
// src/cabrillo/CREDITOS.md.

import { CONT } from './constantes.js';

/**
 * Crea una instancia de análisis independiente sobre un modelo ya
 * construido (ver modelo.js). El modelo puede compartirse entre varias
 * instancias sin problema — lo que nunca se comparte es el estado que
 * devuelve esta fábrica.
 * @param {object} modelo - salida de construirModelo()
 */
export function crearAnalizador(modelo) {
    // ---- estado de ESTA instancia (nunca compartido entre instancias) ----
    // El original arranca mostrando el concurso completo: load() termina
    // llamando set(NB-1) ("Ver todo"), no set(0).
    let cur = modelo.NB - 1;
    let bf = -1; // -1 = todas las bandas, igual que el original
    let velocidad = 1;
    let intervaloId = null;
    const suscriptores = new Set();

    function notificar() {
        const estado = obtenerEstado();
        for (const cb of suscriptores) cb(estado);
    }

    /**
     * Se suscribe a cambios de estado (cursor, filtro, velocidad,
     * reproducción). Devuelve una función para desuscribirse.
     * Mecanismo mínimo, sin dependencias ni framework — vanilla JS.
     */
    function subscribe(cb) {
        suscriptores.add(cb);
        return () => suscriptores.delete(cb);
    }

    // ---- posición temporal ----

    function irA(v) {
        const nuevo = Math.max(0, Math.min(modelo.NB - 1, v));
        if (nuevo === cur) return;
        cur = nuevo;
        notificar();
    }

    function avanzar(delta) {
        irA(cur + delta);
    }

    function verTodo() {
        irA(modelo.NB - 1);
    }

    // ---- filtro de banda ----

    /** @param {number|null} indiceBanda - null/negativo = todas las bandas */
    function filtrarBanda(indiceBanda) {
        const nuevo = indiceBanda == null || indiceBanda < 0 ? -1 : indiceBanda;
        if (nuevo === bf) return;
        bf = nuevo;
        notificar();
    }

    // ---- reproducción ----
    // Misma fórmula de duración que el original: un recorrido completo a
    // 1× dura ~20s, sin importar cuántos bloques tenga el concurso.
    function _calcularIntervaloMs() {
        return 20000 / modelo.NB / velocidad;
    }

    function pausar() {
        if (intervaloId === null) return;
        clearInterval(intervaloId);
        intervaloId = null;
        notificar();
    }

    function reproducir() {
        if (intervaloId !== null) return; // ya estaba reproduciendo
        if (cur >= modelo.NB - 1) irA(0);
        intervaloId = setInterval(() => {
            if (cur >= modelo.NB - 1) {
                pausar();
                return;
            }
            irA(cur + 1);
        }, _calcularIntervaloMs());
        notificar();
    }

    function estaReproduciendo() {
        return intervaloId !== null;
    }

    function fijarVelocidad(v) {
        if (v === velocidad) return;
        velocidad = v;
        if (estaReproduciendo()) {
            // mismo comportamiento que $("spd").onchange en el original:
            // reinicia el intervalo con la nueva velocidad sin perder cur.
            clearInterval(intervaloId);
            intervaloId = null;
            reproducir();
        } else {
            notificar();
        }
    }

    // ---- consultas derivadas (subconjunto visible / acumulados) ----
    // Equivalentes a lo que el original calculaba dentro de stats()/kpis()/
    // drawMap() antes de escribir en el DOM. Acá se devuelven los datos,
    // sin construir HTML ni tocar canvas — eso es tarea de la capa de
    // render (src/cabrillo/render/*.js).

    /**
     * QSO visibles según la posición actual y el filtro de banda.
     * @param {{modo?: 'acc'|'win'|'hr'}} [opciones] - 'acc' (todo hasta cur,
     *   default), 'win' (última hora: 60 min hasta cur) o 'hr' (hora de
     *   reloj completa del cursor) — mismos tres modos que el selector
     *   #md del mapa en el original.
     */
    function qsosVisibles({ modo = 'acc' } = {}) {
        let lo = 0;
        let hi = cur;
        if (modo === 'win') {
            lo = cur - 5;
        } else if (modo === 'hr') {
            const h0 = Math.floor(cur / 6) * 6;
            lo = h0;
            hi = h0 + 5;
        }
        return modelo.D.filter((r) => r[0] <= hi && r[0] >= lo && (bf < 0 || r[1] === bf));
    }

    /** Tablas de stats(): continente×banda, top países, top zonas, total. */
    function estadisticas() {
        const { bands, CN } = modelo;
        const matrizContinenteBanda = CONT.map(() => bands.map(() => 0));
        const porPais = {};
        const porZona = {};
        let totalQso = 0;

        for (const r of modelo.D) {
            if (r[0] > cur || (bf >= 0 && r[1] !== bf)) continue;
            totalQso++;
            matrizContinenteBanda[r[2]][r[1]]++;
            porPais[r[3]] = (porPais[r[3]] || 0) + 1;
            porZona[r[4]] = (porZona[r[4]] || 0) + 1;
        }

        return { matrizContinenteBanda, porPais, porZona, totalQso, nombresPais: CN };
    }

    /** Tarjetas de kpis(): -1 = "Todas", luego una por banda. */
    function kpisPorBanda() {
        const { bands, hr } = modelo;
        const nh = hr[0].length;
        const last = bands.map(() => 0);

        for (const r of modelo.D) {
            if (r[0] <= cur && r[0] > cur - 6) last[r[1]]++;
        }

        function tarjeta(i) {
            const serie = i < 0 ? hr[0].map((_, h) => hr.reduce((s, x) => s + x[h], 0)) : hr[i];
            const pico = Math.max(...serie);
            const horaPico = serie.indexOf(pico);
            const ahora = i < 0 ? last.reduce((s, x) => s + x, 0) : last[i];
            const total = serie.reduce((s, x) => s + x, 0);
            return { indiceBanda: i, ahora, pico, horaPico, media: total / nh, total };
        }

        return [tarjeta(-1), ...bands.map((_, i) => tarjeta(i))];
    }

    function obtenerEstado() {
        return {
            cur,
            bf,
            velocidad,
            reproduciendo: estaReproduciendo(),
            NB: modelo.NB,
        };
    }

    return {
        get modelo() {
            return modelo;
        },
        irA,
        avanzar,
        verTodo,
        filtrarBanda,
        reproducir,
        pausar,
        estaReproduciendo,
        fijarVelocidad,
        qsosVisibles,
        estadisticas,
        kpisPorBanda,
        obtenerEstado,
        subscribe,
    };
}
